import { useState } from 'react'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import { Plus, Trash2, Laptop, Mail, AppWindow, KeyRound } from 'lucide-react'
import { apiClient } from '@/lib/api.ts'
import {
  Badge,
  Button,
  Card,
  EmptyState,
  IconButton,
  Input,
  LoadingState,
  Modal,
  PageHeader,
  Select,
  useToast,
} from '@/components/ui'

type Category = 'hardware' | 'm365' | 'software' | 'rights_access'

interface ProfileItem {
  id: string
  profile_id: string
  category: Category
  item_key: string
  label: string
  action: 'add' | 'remove' | 'replace'
}

interface RoleProfile {
  id: string
  scope: 'global' | 'tenant'
  name: string
  description: string | null
  is_active: number
  items: ProfileItem[]
}

const CATEGORIES: { value: Category; label: string; icon: React.ElementType }[] = [
  { value: 'hardware', label: 'Hardware', icon: Laptop },
  { value: 'm365', label: 'M365 Lizenz/Gruppen', icon: Mail },
  { value: 'software', label: 'Software', icon: AppWindow },
  { value: 'rights_access', label: 'Rechte / Zutritt', icon: KeyRound },
]

export function RoleProfilesPage() {
  const qc = useQueryClient()
  const { toast } = useToast()
  const [createOpen, setCreateOpen] = useState(false)
  const [newName, setNewName] = useState('')
  const [newScope, setNewScope] = useState<'global' | 'tenant'>('global')

  const { data: profiles, isLoading } = useQuery<RoleProfile[]>({
    queryKey: ['proc-role-profiles'],
    queryFn: () => apiClient.get<RoleProfile[]>('/prozesse/role-profiles').then((r) => r.data),
  })

  const refresh = () => { void qc.invalidateQueries({ queryKey: ['proc-role-profiles'] }) }

  async function createProfile() {
    if (!newName.trim()) { toast({ title: 'Name ist erforderlich', tone: 'danger' }); return }
    try {
      await apiClient.post('/prozesse/role-profiles', { name: newName.trim(), scope: newScope })
      setCreateOpen(false); setNewName(''); setNewScope('global')
      refresh()
    } catch {
      toast({ title: 'Profil konnte nicht angelegt werden', tone: 'danger' })
    }
  }

  async function deleteProfile(id: string) {
    try {
      await apiClient.delete(`/prozesse/role-profiles/${id}`)
      refresh()
    } catch {
      toast({ title: 'Profil konnte nicht gelöscht werden', tone: 'danger' })
    }
  }

  return (
    <div className="space-y-4">
      <PageHeader
        title="Rollen-Profile"
        breadcrumbs={[{ label: 'Prozesse', to: '/prozesse' }, { label: 'Rollen-Profile' }]}
        actions={
          <Button variant="primary" leadingIcon={<Plus className="w-4 h-4" />} onClick={() => setCreateOpen(true)}>
            Profil anlegen
          </Button>
        }
      />

      <p className="text-sm text-neutral-600">
        Rollen-Profile bündeln Standard-Sets (Hardware, M365-Lizenzen/Gruppen, Software, Rechte/Zutritt)
        je Rolle. Globale Profile gelten überall; mandantenspezifische Profile überschreiben sie für
        einzelne Kunden. On-/Offboarding-Prozesse ziehen das passende Set automatisch.
      </p>

      {isLoading ? (
        <LoadingState label="Profile werden geladen…" />
      ) : !profiles || profiles.length === 0 ? (
        <EmptyState
          icon={<KeyRound className="w-8 h-8" />}
          title="Noch keine Rollen-Profile"
          description="Lege ein Profil an, um Standard-Sets für Rollen zu pflegen."
        />
      ) : (
        <div className="space-y-3">
          {profiles.map((p) => (
            <ProfileCard key={p.id} profile={p} onChanged={refresh} onDelete={() => { void deleteProfile(p.id) }} />
          ))}
        </div>
      )}

      <Modal
        open={createOpen}
        onClose={() => setCreateOpen(false)}
        title="Rollen-Profil anlegen"
        footer={
          <>
            <Button variant="ghost" onClick={() => setCreateOpen(false)}>Abbrechen</Button>
            <Button variant="primary" onClick={createProfile}>Anlegen</Button>
          </>
        }
      >
        <div className="space-y-3">
          <Input label="Name" value={newName} onChange={(e) => setNewName(e.target.value)} placeholder="z.B. Techniker" />
          <Select
            label="Geltungsbereich"
            value={newScope}
            onValueChange={(v) => setNewScope(v as 'global' | 'tenant')}
            options={[
              { value: 'global', label: 'Global (alle Mandanten)' },
              { value: 'tenant', label: 'Nur dieser Mandant' },
            ]}
          />
        </div>
      </Modal>
    </div>
  )
}

function ProfileCard({ profile, onChanged, onDelete }: { profile: RoleProfile; onChanged: () => void; onDelete: () => void }) {
  const { toast } = useToast()
  const [category, setCategory] = useState<Category>('hardware')
  const [itemKey, setItemKey] = useState('')
  const [label, setLabel] = useState('')

  async function addItem() {
    if (!itemKey.trim() || !label.trim()) { toast({ title: 'Schlüssel und Bezeichnung erforderlich', tone: 'danger' }); return }
    try {
      await apiClient.post(`/prozesse/role-profiles/${profile.id}/items`, { category, itemKey: itemKey.trim(), label: label.trim() })
      setItemKey(''); setLabel('')
      onChanged()
    } catch {
      toast({ title: 'Eintrag konnte nicht hinzugefügt werden', tone: 'danger' })
    }
  }

  async function deleteItem(itemId: string) {
    try {
      await apiClient.delete(`/prozesse/role-profiles/${profile.id}/items/${itemId}`)
      onChanged()
    } catch {
      toast({ title: 'Eintrag konnte nicht gelöscht werden', tone: 'danger' })
    }
  }

  return (
    <Card>
      <Card.Header>
        <div className="flex items-center gap-2">
          <span className="font-semibold text-neutral-900">{profile.name}</span>
          <Badge variant={profile.scope === 'global' ? 'neutral' : 'info'}>
            {profile.scope === 'global' ? 'Global' : 'Mandant'}
          </Badge>
          <div className="ml-auto">
            <IconButton icon={<Trash2 />} aria-label="Profil löschen" variant="ghost" size="sm" onClick={onDelete} />
          </div>
        </div>
      </Card.Header>
      <Card.Body className="space-y-4">
        {CATEGORIES.map(({ value, label: catLabel, icon: Icon }) => {
          const items = profile.items.filter((i) => i.category === value)
          return (
            <div key={value}>
              <div className="flex items-center gap-1.5 text-xs font-semibold text-neutral-500 uppercase tracking-wide mb-1.5">
                <Icon className="w-3.5 h-3.5" />
                {catLabel}
              </div>
              {items.length === 0 ? (
                <p className="text-xs text-neutral-400 pl-5">—</p>
              ) : (
                <div className="space-y-1 pl-5">
                  {items.map((it) => (
                    <div key={it.id} className="flex items-center gap-2 text-sm">
                      <span className="text-neutral-800 flex-1">{it.label}</span>
                      <code className="text-xs text-neutral-400">{it.item_key}</code>
                      {it.action !== 'add' && <Badge variant="warning">{it.action}</Badge>}
                      <IconButton icon={<Trash2 />} aria-label="Eintrag löschen" variant="ghost" size="sm" onClick={() => deleteItem(it.id)} />
                    </div>
                  ))}
                </div>
              )}
            </div>
          )
        })}
      </Card.Body>
      <Card.Footer>
        <div className="grid grid-cols-1 sm:grid-cols-4 gap-2 w-full items-end">
          <Select
            label="Kategorie"
            value={category}
            onValueChange={(v) => setCategory(v as Category)}
            options={CATEGORIES.map((c) => ({ value: c.value, label: c.label }))}
          />
          <Input label="Bezeichnung" value={label} onChange={(e) => setLabel(e.target.value)} placeholder="z.B. ThinkPad T14" />
          <Input label="Schlüssel" value={itemKey} onChange={(e) => setItemKey(e.target.value)} placeholder="z.B. laptop_standard" />
          <Button variant="secondary" leadingIcon={<Plus className="w-4 h-4" />} onClick={addItem}>
            Hinzufügen
          </Button>
        </div>
      </Card.Footer>
    </Card>
  )
}
